import { useApolloClient } from '@apollo/client';
import { HeaderBackButton } from '@react-navigation/elements';
import { StackNavigationOptions } from '@react-navigation/stack';
import * as Sentry from '@sentry/core';
import { useEffect } from 'react';
import { Platform } from 'react-native';

import { RoundedSectionNavigationOptions } from '@oui/app-core/src/components/RoundedSection';
import { DEFAULT_HEADER_MODE, SESSION_TIMEOUT } from '@oui/app-core/src/constants';
import { useCurrentUser } from '@oui/app-core/src/hooks/useCurrentUser';
import { usePersistedState } from '@oui/app-core/src/hooks/usePersistedState';
import { createSessionTimeoutStackNavigator } from '@oui/app-core/src/lib/createSessionTimeoutNavigator';
import { useDefaultHeaderHeight } from '@oui/app-core/src/lib/getDefaultHeaderHeight';
import { useI18n } from '@oui/app-core/src/lib/i18n';
import { setDeviceInfo } from '@oui/app-core/src/lib/setDeviceInfo';
import { AccountSettings } from '@oui/app-core/src/screens/AccountSettings';
import { Auth } from '@oui/app-core/src/screens/Auth';
import { ContactsPicker } from '@oui/app-core/src/screens/ContactsPicker';
import { Conversation } from '@oui/app-core/src/screens/Conversation';
import { CreateTestUser } from '@oui/app-core/src/screens/CreateTestUser';
import { EditLessonsLearned } from '@oui/app-core/src/screens/EditLessonsLearned';
import { LocalAuthenticationPrompt } from '@oui/app-core/src/screens/LocalAuthenticationPrompt';
import { QuizSet } from '@oui/app-core/src/screens/QuizSet';
import { TermsAndPrivacy } from '@oui/app-core/src/screens/TermsAndPrivacy';
import { TestArtifactResult } from '@oui/app-core/src/screens/TestArtifactResult';
import { useTheme } from '@oui/app-core/src/styles';
import { StaticSession } from '@oui/app-static/src/screens/StaticSession';
import { registerStressBreathingScreens } from '@oui/stress-breathing';
import { registerWordPairingScreens } from '@oui/word-pairing';

import { TabNavigator } from '@src/components/TabNavigator';
import { Confidentiality } from '@src/screens/Confidentiality';
import { FinishRegistering } from '@src/screens/FinishRegistering';
import { Welcome } from '@src/screens/Welcome';
import { RootStackParamList } from '@src/types/navigation';

import { StressSensitivityAssessment } from '../screens/StressSensitivityAssessment/StressSensitivityAssessment';

const Stack = createSessionTimeoutStackNavigator<RootStackParamList>(SESSION_TIMEOUT);
export const RootNavigator = () => {
  const { scheme, theme } = useTheme();
  const { isLoggedIn } = useCurrentUser();
  const defaultHeaderHeight = useDefaultHeaderHeight();
  const { $t } = useI18n();
  const apollo = useApolloClient();

  const [hasSeenLocalAuthenticationPrompt] = usePersistedState(
    'SeenLocalAuthenticationPrompt',
    false,
  );

  useEffect(() => {
    if (isLoggedIn) {
      setDeviceInfo(apollo).catch(Sentry.captureException);
    }
  }, [apollo, isLoggedIn]);

  return (
    <Stack.Navigator
      {...Platform.select({ web: { cardStyle: { flex: 1 } } })} // So web screens grow properly
      screenOptions={() => {
        const options: StackNavigationOptions = {
          headerStyle: {
            height: defaultHeaderHeight,
          },
          headerBackTestID: 'header-back',
          headerTruncatedBackTitle: $t({ id: 'App_backButtonLabel', defaultMessage: 'Back' }),
          headerTintColor: theme.color.primary100,
          headerTitleStyle: { color: theme.color.dark, fontFamily: 'OpenSansSemiBold' },
          headerMode: DEFAULT_HEADER_MODE,
        };
        return options;
      }}
    >
      {isLoggedIn ? (
        <>
          {hasSeenLocalAuthenticationPrompt ? null : (
            <Stack.Screen
              name="LocalAuthenticationPrompt"
              component={LocalAuthenticationPrompt}
              options={{ headerShown: false }}
            />
          )}
          <Stack.Screen
            name="home"
            component={TabNavigator}
            options={{ headerShown: false, title: '' }}
          />
          <Stack.Screen name="TermsAndPrivacy" component={TermsAndPrivacy} />
          <Stack.Screen
            name="Confidentiality"
            component={Confidentiality}
            options={{ headerShown: false }}
          />
          <Stack.Screen
            name="FinishPatientRegistration"
            component={FinishRegistering}
            options={{ headerShown: false }}
          />
          <Stack.Screen
            name="Conversation"
            component={Conversation}
            options={({ route, navigation }) => ({
              headerBackTitleVisible: false,
              title:
                route.params?.title ??
                route.params?.ID ??
                $t({ id: 'Conversation_title', defaultMessage: 'Conversation' }),
              // headerRight: ({ tintColor }) => (
              //   <HeaderButtons>
              //     <HeaderItem
              //       iconName="practice"
              //       color={tintColor}
              //       title=""
              //       onPress={() => navigation.navigate('home', { screen: 'Practice' })}
              //       aria-label={$t({
              //         id: 'Header_practiceButton',
              //         defaultMessage: 'View Practice',
              //       })}
              //       testID="Header_practiceButton"
              //     />
              //   </HeaderButtons>
              // ),
            })}
          />
          <Stack.Screen name="AccountSettings" component={AccountSettings} />
          <Stack.Screen name="EditLessonsLearned" component={EditLessonsLearned} />
          <Stack.Screen
            name="QuizSet"
            component={QuizSet}
            options={RoundedSectionNavigationOptions({
              defaultHeaderHeight,
              tintColor: 'white',
              backgroundColor: 'black',
            })}
            initialParams={{}}
          />
          <Stack.Screen
            name="ContactsPicker"
            component={ContactsPicker}
            options={() => ({
              title: $t({ id: 'ContactsPicker_title', defaultMessage: 'Contacts' }),
              headerLeft: () => <HeaderBackButton onPress={() => {}} />,
              headerRight: undefined,
            })}
          />
          <Stack.Screen name="TestArtifactResult" component={TestArtifactResult} />
          <Stack.Screen
            name="StressSensitivityAssessment"
            component={StressSensitivityAssessment}
          />
          <Stack.Screen
            name="StaticSession"
            component={StaticSession}
            options={({ route }) => ({
              headerTitle: route.params.title,
              headerBackTitleVisible: false,
            })}
          />
          {registerWordPairingScreens(Stack)}
          {registerStressBreathingScreens(Stack)}
        </>
      ) : (
        <>
          {Auth({ $t, Screen: Stack.Screen, scheme })}
          <Stack.Screen
            name="CreateTestUser"
            component={CreateTestUser}
            options={{ headerShown: false }}
          />
          <Stack.Screen name="Welcome" component={Welcome} options={{ headerShown: false }} />
        </>
      )}
    </Stack.Navigator>
  );
};
